import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import posthog from 'posthog-js'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ChartLine,
  Check,
  ClipboardList,
  Copy,
  FileCode,
  Flag,
  IdCard,
  Image,
  LayoutDashboard,
  Loader2,
  Plus,
  ShoppingBag,
  Stethoscope,
  Trash2,
  TrendingUp,
} from 'lucide-react'

import { CreateObjectButton } from '@/components/CreateObjectButton'
import { SearchSelect } from '@/components/SearchSelect'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardHeader, CardIcon, CardTitle, CardAction, CardContent, CardFooter } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { StatsBar, StatsBarId, StatsBarActivity } from '../../components/StatsBar'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AuthService } from '@/services/core/auth'
import {
  LineItemsApi,
  LineItem,
  LineItemInput,
  LineItemType,
  DeliveryCurve,
  CreativeDelivery,
  CreativeWeighting,
  BidSpecification,
  LineItemObjectives,
  DEFAULT_BID_STRATEGY,
  DEFAULT_BIDDING_GOAL,
  biddingGoalOf,
  biddingEventTargetOf,
  deliveryCurveOf,
} from '@/services/lineItems/lineItemsApi'
import {
  BID_FIELDS,
  OBJECTIVE_TYPES,
  type BidField,
  type ObjectiveType,
} from '@/services/networks/networkFieldSettings'
import {
  budgetFlight,
  budgetPeriodsForSave,
  budgetTotal,
  mapBudgetPeriods,
  type BudgetPeriod,
} from '@/services/lineItems/budgets'
import { BudgetPeriodsTable } from '@/components/budgets/BudgetPeriodsTable'
import { BudgetPeriodsDialog } from '@/components/budgets/BudgetPeriodsDialog'
import { OrdersApi } from '@/services/orders/ordersApi'
import { CreativesApi } from '@/services/creatives/creativesApi'
import { AssetsApi, isVideoAsset } from '@/services/assets/assetsApi'
import { DataViewApi, type LineItemDataView } from '@/services/core/dataViewApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { CreativeTable, type CreativeAssetPreview } from '../../components/CreativeTable'
import { CreativeTagDownloadMenu } from '../../components/CreativeTagDownloadMenu'
import { DcoGenerateDialog } from '../../components/DcoGenerateDialog'
import { RetailProductPickerDialog } from '../../components/RetailProductPickerDialog'
import { useHasRetailProducts } from '../../hooks/useHasRetailProducts'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton, type StatusFilterValue } from '../../components/StatusFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { filterBySearch } from '../../utils/search'
import { AppShell } from '../../components/AppShell'
import { StatsPanel } from '../../components/StatsPanel'
import { DeliveryBreakdownCard } from '../../components/DeliveryBreakdownCard'
import { DeliveryProgressCard } from '../../components/DeliveryProgressCard'
import { FlightOutcomeBand } from '../../components/FlightOutcome'
import { NotesSection } from '../../components/NotesSection'
import { AuditHistoryCard } from '../../components/AuditHistoryCard'
import { ListTabs } from '../../components/ListTabs'
import { ReportsCard } from '../../components/reports/ReportsCard'
import { LineItemDiagnosticsCard } from '../../components/diagnostics/LineItemDiagnosticsCard'
import { LineItemAdRequestDiagnostics } from '../../components/diagnostics/LineItemAdRequestDiagnostics'
import { DateRangeSelect } from '../../components/DateRangeSelect'
import { useCreativeStats } from '../../hooks/useCreativeStats'
import { useStatsRange } from '../../hooks/useStatsRange'
import type { DateRange } from '@/services/stats/statsRange'
import type { ReportTarget } from '@/services/reports/reportsApi'
import { useResolvedRange } from '../../hooks/useResolvedRange'
import { DateTimePicker } from '../../components/DateTimePicker'
import { CurrencyAmountInput } from '../../components/CurrencyAmountInput'
import { CurrencySelect } from '../../components/CurrencySelect'
import { FormSkeleton } from '../../components/FormSkeleton'
import { FieldSkeleton } from '../../components/FieldSkeleton'
import { ShareOfVoiceInput } from '../../components/ShareOfVoiceInput'
import { FieldLabel } from '../../components/FieldLabel'
import { ValidationWarnings } from '../../components/ValidationWarnings'
import { InlineSelectField } from '../../components/InlineSelectField'
import { OptionalField } from '../../components/OptionalField'
import { AddFieldMenu } from '../../components/AddFieldMenu'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { useFieldLimitText } from '../../hooks/useFieldLimitText'
import { useDefaultCurrency } from '../../hooks/useDefaultCurrency'
import { useLineItemFieldSettings } from '../../hooks/useLineItemFieldSettings'
import { useBiddingCustomEvents } from '../../hooks/useBiddingCustomEvents'
import {
  MarketplaceProductSelect,
  AX_PRODUCTS_QUERY_KEY,
} from '../../components/MarketplaceProductSelect'
import { AxProductsApi } from '@/services/earnings/axProductsApi'
import { ElectionSelect } from '../../components/political/ElectionSelect'
import { ElectionTopicSelect } from '../../components/political/ElectionTopicSelect'
import { useElectionTopics } from '../../hooks/useElectionTopics'
import { topicCounts } from '@/services/political/politicalTopics'
import {
  applyProductConstraints,
  bidStrategiesForProduct,
  grossUpCpmFloor,
  lineItemTypeForProduct,
  productTargetingLimits,
} from '@/services/lineItems/productConstraints'
import { useNetwork } from '../../contexts/NetworkContext'
import { useCurrentUser } from '../../contexts/UserContext'
import { isSuperAdmin } from '../../data/superAdmins'
import { TargetingCard } from '../../components/targeting/TargetingCard'
import { CappingCard } from '../../components/capping/CappingCard'
import { BidStrategyFields } from '../../components/BidStrategyFields'
import {
  hasRateLimitProblem,
  rateLimitsForSave,
  type RateLimit,
} from '@/services/earnings/rateLimits'
import {
  hiddenTargetingTypes,
  targetingForSave,
  typesInUse,
  type Targeting,
} from '@/services/targeting/targeting'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { startOfToday } from 'date-fns'
import { CpmFeeBreakdown } from '../../components/CpmFeeBreakdown'
import { CpmFeesDialog } from '../../components/CpmFeesDialog'
import { feesForSave, type Fee } from '@/services/earnings/fees'
import { useCpmBreakdown } from '../../hooks/useCpmBreakdown'
import {
  MARKETPLACE_USER_STATES,
  UserStateSelect,
  type UserState,
} from '../../components/UserStateSelect'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { PoliticalBadge } from '@/components/political/PoliticalBadge'
import { PoliticalLinkStatus } from '@/components/political/PoliticalLinkStatus'
import { AdvertisersApi } from '@/services/advertisers/advertisersApi'
import { useObjectCrumbs } from '../../hooks/useObjectCrumbs'
import { formatDate, formatNumber } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { LineItemCopyDialog } from '../../components/LineItemCopyDialog'
import { TagLineItemButton } from '@/components/TagLineItemButton'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

/** A budget is one total, or — on marketplace — split over periods. */
type BudgetMode = 'single' | 'periods'

const LINE_ITEM_TYPES: LineItemType[] = ['AUCTION', 'SPONSORSHIP', 'CREATIVE_TAG']
const CREATIVE_DELIVERIES: CreativeDelivery[] = ['NONE', 'CREATIVE', 'LINE_ITEM']
const CREATIVE_WEIGHTINGS: CreativeWeighting[] = ['EQUAL', 'CLICK']

// ── Creatives panel ──────────────────────────────────────────────────────────

function LineItemCreativesPanel({
  lineItem,
  range: controlledRange,
  onRangeChange,
  delivery,
  onDeliveryChange,
  weighting,
  onWeightingChange,
}: {
  lineItem: LineItem
  /**
   * Period to show delivery for. Pass this with `onRangeChange` to share the
   * page's period; leave both out and the panel keeps its own, remembered one.
   */
  range?: DateRange
  onRangeChange?: (range: DateRange) => void
  /**
   * The line item's creative rotation settings. They belong to the line item
   * rather than to any one creative, so the page owns them and the panel shows
   * them in its footer, under the list they govern.
   */
  delivery: CreativeDelivery
  onDeliveryChange: (value: CreativeDelivery) => void
  weighting: CreativeWeighting
  onWeightingChange: (value: CreativeWeighting) => void
}) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const lineItemId = lineItem.id
  const advertiserId = lineItem.advertiserId
  const queryClient = useQueryClient()
  const [selection, setSelection] = useState<typeof creatives>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [dcoOpen, setDcoOpen] = useState(false)
  const [retailOpen, setRetailOpen] = useState(false)
  const hasRetailProducts = useHasRetailProducts()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = usePersistedState<StatusFilterValue>(
    'filters.lineItemCreatives.status',
    'ACTIVE'
  )
  const [ownRange, setOwnRange] = useStatsRange('today', {
    startDate: lineItem.startDate,
    endDate: lineItem.endDate,
  })
  const statsRange = controlledRange ?? ownRange
  const setStatsRange = onRangeChange ?? setOwnRange

  // Delivery per creative for the picked period — loaded whenever the line item is opened.
  const resolvedStatsRange = useResolvedRange(statsRange, {
    startDate: lineItem.startDate,
    endDate: lineItem.endDate,
  })
  const { statsByCreative } = useCreativeStats(lineItemId, resolvedStatsRange)

  const { data: creatives = [], isLoading } = useQuery({
    queryKey: ['lineitem-creatives', lineItemId],
    queryFn: () => CreativesApi.getByLineItem(lineItemId),
  })

  const filtered = filterBySearch(
    statusFilter === 'all' ? creatives : creatives.filter((c) => c.objectState === statusFilter),
    search
  )
  const existingCreativeIds = useMemo(() => new Set(creatives.map((c) => c.id)), [creatives])

  // Single query: fetch dataView then resolve preview assets for each creative
  const { data: previewAssets = {} } = useQuery({
    queryKey: ['lineitem-preview-assets', lineItemId],
    queryFn: async () => {
      const dataView = await DataViewApi.fetchOne<LineItemDataView>('LineItemView', lineItemId)
      if (!dataView?.creatives) return {}
      const entries = await Promise.all(
        Object.entries(dataView.creatives).map(async ([creativeId, creative]) => {
          const assetRef = Object.values(creative.constraintsToAssets ?? {})[0]
          if (!assetRef?.id) return null
          try {
            const asset = await AssetsApi.getByCreativeAndAsset(creativeId, assetRef.id)
            const src = isVideoAsset(asset.mimeType ?? '')
              ? asset.cdnId
              : asset.previewCdnId || asset.cdnId
            if (!src) return null
            return [
              creativeId,
              {
                src,
                mimeType: asset.mimeType,
                fileSize: asset.fileSize,
                width: asset.width,
                height: asset.height,
              },
            ] as [string, CreativeAssetPreview]
          } catch {
            return null
          }
        })
      )
      return Object.fromEntries(
        entries.filter((e): e is [string, CreativeAssetPreview] => e !== null)
      )
    },
    staleTime: 5 * 60 * 1000,
  })

  const handleBulkStateChange = async (objectState: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      const updated = await Promise.all(
        selection.map((c) => CreativesApi.update(c.id, { name: c.name, objectState }))
      )
      queryClient.setQueryData(
        ['lineitem-creatives', lineItemId],
        (old: typeof creatives | undefined) => {
          if (!old) return old
          const updatedIds = new Set(updated.map((u) => u.id))
          return old.map((c) =>
            updatedIds.has(c.id) ? (updated.find((u) => u.id === c.id) ?? c) : c
          )
        }
      )
      const key = objectState === 'ACTIVE' ? 'doneDetail_activate' : 'doneDetail_deactivate'
      toast.success(t('common.done'), {
        description: t(`creatives.${key}`, { count: selection.length }),
      })
      setSelection([])
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <ListCard tone="creative">
      <ListCardHeader
        title={
          <>
            <CardIcon icon={Image} />
            {t('creatives.creativeCount', { count: filtered.length })}
          </>
        }
        controls={
          <>
            <StatusFilterButton
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v)
                setSelection([])
              }}
            />
            <SearchInput value={search} onChange={setSearch} />
          </>
        }
        trailingControls={
          <DateRangeSelect
            value={statsRange}
            onChange={setStatsRange}
            flight={{ startDate: lineItem.startDate, endDate: lineItem.endDate }}
          />
        }
        actions={
          <>
            <CreativeTagDownloadMenu
              creatives={filtered}
              tagContext={{
                parentType: 'lineItemId',
                parentId: lineItemId,
                networkTagId: currentNetwork?.tagId ?? '',
                targeting: true,
              }}
              downloadContext={{
                lineItem: {
                  id: lineItemId,
                  name: lineItem.name,
                  startDate: lineItem.startDate,
                  endDate: lineItem.endDate,
                },
                order: { id: lineItem.orderId, name: lineItem.orderName },
                advertiser: { id: lineItem.advertiserId, name: lineItem.advertiserName },
              }}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => setDcoOpen(true)}>
              <FileCode className="size-4" /> {t('creatives.dco.open')}
            </Button>
            {hasRetailProducts && (
              <Button type="button" variant="outline" size="sm" onClick={() => setRetailOpen(true)}>
                <ShoppingBag className="size-4" /> {t('retailProducts.add')}
              </Button>
            )}
            <CreateObjectButton type="creative" context={{ lineItem: lineItemId, advertiser: advertiserId }} />
          </>
        }
      />
      <DcoGenerateDialog
        open={dcoOpen}
        onOpenChange={setDcoOpen}
        lineItemId={lineItemId}
        existingCreativeIds={existingCreativeIds}
      />
      {hasRetailProducts && (
        <RetailProductPickerDialog
          open={retailOpen}
          onOpenChange={setRetailOpen}
          lineItemId={lineItemId}
        />
      )}
      {selection.length > 0 && (
        <BulkActionBar
          count={selection.length}
          onClear={() => setSelection([])}
          loading={bulkLoading}
          onActivate={() => handleBulkStateChange('ACTIVE')}
          onDeactivate={() => handleBulkStateChange('INACTIVE')}
        />
      )}
      <CardContent className="p-0">
        <CreativeTable
          creatives={filtered}
          loading={isLoading}
          showAdvertiser={false}
          showPreview
          previewOnBlankPage
          previewAssets={previewAssets}
          emptyMessage={t('creatives.noCreativesFound')}
          selection={selection}
          onSelectionChange={setSelection}
          stats={statsByCreative}
          columnsKey="lineItemCreatives"
        />
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t">
        <InlineSelectField
          label={t('lineItems.creativeWeighting')}
          tooltip={t('lineItems.creativeWeightingHelp')}
          value={weighting}
          onChange={onWeightingChange}
          options={CREATIVE_WEIGHTINGS.map((value) => ({
            value,
            label: t(`lineItems.creativeWeightings.${value}`),
          }))}
        />
        <InlineSelectField
          label={t('lineItems.creativeDelivery')}
          tooltip={t('lineItems.creativeDeliveryHelp')}
          value={delivery}
          onChange={onDeliveryChange}
          options={CREATIVE_DELIVERIES.map((value) => ({
            value,
            label: t(`lineItems.creativeDeliveries.${value}`),
          }))}
        />
      </CardFooter>
    </ListCard>
  )
}

// ── Inner form ───────────────────────────────────────────────────────────────

/** The page's tabs; the first is the default, left out of the URL. */
const PAGE_TABS = ['overview', 'stats', 'diagnostics'] as const
type PageTab = (typeof PAGE_TABS)[number]

interface LineItemEditFormProps {
  lineItem: LineItem | null
  id: string
  isNew: boolean
  initialOrderId?: string
}

function LineItemEditForm({ lineItem, id, isNew, initialOrderId = '' }: LineItemEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  // Budget periods are calendar days in the network's zone, and so are the
  // line item dates derived from them.
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)
  const currentUser = useCurrentUser()
  // Overriding a booked product's targeting is an Adnuntius-side escape hatch,
  // so only the super admin is offered the switch.
  const canIgnoreProduct = isSuperAdmin(currentUser?.username)
  // Supplementary fees are ad ops' to set, as in the old UI; new ones only on
  // marketplace networks. The API itself only requires MANAGE_ADVERTISING.
  const isAdOps =
    !!currentNetwork && !!currentUser?.permissionsByNetwork[currentNetwork.tagId]?.adOps
  // The copy endpoint wants one of the advertising write permissions, so a
  // read-only advertiser is not offered a button the server would refuse.
  const canCopy =
    !!currentNetwork &&
    !!currentUser?.permissionsByNetwork[currentNetwork.tagId]?.advertisingEditable
  const [feesDialogOpen, setFeesDialogOpen] = useState(false)
  const [lineItemType, setLineItemType] = useState<LineItemType>(
    (lineItem?.type ?? 'AUCTION') as LineItemType
  )
  // Whether to save past the booked product's targeting enforcement. Not a
  // field on the line item — it is a query parameter of the save — so it lives
  // outside the form, and the submit handler reads it through a ref.
  const [ignoreProductChoice, setIgnoreProductChoice] = useState<boolean | null>(null)
  const ignoreProductRef = useRef(false)
  // Filled in below, once the product and the owning advertiser/team are known.
  const currencyRef = useRef('')
  // One period for the whole page, so the chart, the targeting totals under it
  // and the per-creative delivery always cover the same span. Shared with every
  // other stats view, so the choice — not least "the campaign period" — holds
  // as you click from one line item to the next.
  const [statsRange, setStatsRange] = useStatsRange('24h', {
    startDate: lineItem?.startDate,
    endDate: lineItem?.endDate,
  })

  // The overview holds the form, so it is only ever hidden, never unmounted;
  // the stats and diagnostics tabs are mounted while open. The open tab is in
  // the URL (`?tab=`, left out for the overview), as RouteTabs keeps it, so a
  // link or a reload lands on the same tab.
  const [searchParams, setSearchParams] = useSearchParams()
  const pageTab: PageTab = PAGE_TABS.find((tab) => tab === searchParams.get('tab')) ?? 'overview'
  const setPageTab = (tab: PageTab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (tab === 'overview') next.delete('tab')
        else next.set('tab', tab)
        return next
      },
      { replace: true }
    )

  // The creatives the line item already has, for marking the sizes it can fill.
  // Shares the creatives panel's query key, so this costs no extra request.
  const { data: lineItemCreatives = [] } = useQuery({
    queryKey: ['lineitem-creatives', id],
    queryFn: () => CreativesApi.getByLineItem(id),
    enabled: !isNew && !!id,
  })
  const creativeSizes = useMemo(
    () =>
      lineItemCreatives
        .filter((creative) => creative.width > 0 && creative.height > 0)
        .map((creative) => `${creative.width}x${creative.height}`),
    [lineItemCreatives]
  )

  const resolvedStatsRange = useResolvedRange(statsRange, {
    startDate: lineItem?.startDate,
    endDate: lineItem?.endDate,
  })

  const { data: orderList = [] } = useQuery({
    queryKey: ['orders'],
    queryFn: () => OrdersApi.getAll(),
  })
  // The list holds only the first page of active orders, so the order a new
  // line item was started from may be missing — fetch it on its own.
  const listHasInitialOrder = orderList.some((o) => o.id === initialOrderId)
  const { data: initialOrder } = useQuery({
    queryKey: ['order', initialOrderId],
    queryFn: () => OrdersApi.getById(initialOrderId),
    enabled: !!initialOrderId && !listHasInitialOrder,
  })
  const orders = useMemo(
    () => (initialOrder && !listHasInitialOrder ? [initialOrder, ...orderList] : orderList),
    [initialOrder, listHasInitialOrder, orderList]
  )

  const form = useForm({
    defaultValues: {
      name: lineItem?.name ?? '',
      externalReference: lineItem?.externalReference ?? '',
      dealId: lineItem?.dealId ?? '',
      // Political: the election the ad is for, and the topic within it.
      electionId: lineItem?.electionId ?? '',
      politicalCampaignTopic: lineItem?.politicalCampaignTopic ?? '',
      orderId: lineItem?.orderId ?? initialOrderId,
      axProductId: lineItem?.axProductId ?? '',
      type: (lineItem?.type ?? 'AUCTION') as LineItemType,
      // What the user says should happen with the line item. The API defaults a
      // new line item to PROPOSED, and the network can default it to something else.
      userState: (lineItem?.userState ?? '') as UserState | '',
      startDate: lineItem?.startDate ?? '',
      endDate: lineItem?.endDate ?? '',
      // Budget split over periods. When it is set, the two dates above are
      // derived from it rather than edited, which is the only pairing the API
      // accepts — see `budgetFlight`.
      budget: (lineItem?.budget ?? []) as BudgetPeriod[],
      budgetRollover: lineItem?.budgetRollover ?? true,
      // One currency covers every amount on the line item; '' means "use the derived default".
      currency:
        BID_FIELDS.map((f) => lineItem?.bidSpecification?.[f]?.currency).find(Boolean) ??
        lineItem?.objectives?.BUDGET?.currency ??
        lineItem?.billingCurrency ??
        '',
      // Bidding strategy, and the goal a dynamic strategy optimises towards.
      bidStrategy: lineItem?.bidSpecification?.bidStrategy ?? DEFAULT_BID_STRATEGY,
      biddingGoal: biddingGoalOf(lineItem?.bidSpecification) ?? DEFAULT_BIDDING_GOAL,
      // Bid — keyed by bid field, so the network settings can drive which show.
      bids: Object.fromEntries(
        BID_FIELDS.map((f) => [f, lineItem?.bidSpecification?.[f]?.amount?.toString() ?? ''])
      ) as Record<BidField, string>,
      // The fees on the gross CPM that are set here rather than derived by the
      // server — posted back whole, so a removed fee is really removed.
      cpmFees: (lineItem?.bidSpecification?.cpmFees ?? []) as Fee[],
      supplementaryFees: (lineItem?.bidSpecification?.supplementaryFees ?? []) as Fee[],
      // Objectives — same, keyed by objective type.
      objectives: Object.fromEntries(
        OBJECTIVE_TYPES.map((type) => [
          type,
          (type === 'BUDGET'
            ? lineItem?.objectives?.BUDGET?.amount
            : (lineItem?.objectives?.[type] as number | undefined)
          )?.toString() ?? '',
        ])
      ) as Record<ObjectiveType, string>,
      // Targeting — held as the API's own object and posted back whole.
      targeting: (lineItem?.targeting ?? {}) as Targeting,
      // Delivery caps — also posted whole, so a removed cap is really removed.
      rateLimits: (lineItem?.rateLimits ?? []) as RateLimit[],
      // Delivery
      sponsorshipPercentage: lineItem?.sponsorshipPercentage?.toString() ?? '',
      // The API derives `smoothed` from the curve, so the curve is what we edit.
      deliveryCurve: deliveryCurveOf(lineItem),
      // Creative rotation — edited in the footer of the creatives list.
      creativeDelivery: (lineItem?.creativeDelivery ?? 'NONE') as CreativeDelivery,
      creativeWeighting: (lineItem?.creativeWeighting ?? 'EQUAL') as CreativeWeighting,
    },
    onSubmit: ({ value }) => {
      const sponsorship = parseFloat(value.sponsorshipPercentage)
      const currency = currencyRef.current

      const bidSpecification: BidSpecification = {}
      for (const field of BID_FIELDS) {
        const amount = parseFloat(value.bids[field])
        if (value.bids[field] && !isNaN(amount)) bidSpecification[field] = { amount, currency }
      }
      bidSpecification.cpmFees = feesForSave(value.cpmFees, currency)
      // The API drops supplementary fees from anyone but ad ops, so only they send them.
      if (isAdOps) {
        bidSpecification.supplementaryFees = feesForSave(value.supplementaryFees, currency)
      }
      if (bidStrategyChoices.length) {
        bidSpecification.bidStrategy = value.bidStrategy
        // The goal is only read for a dynamic strategy, and the API keeps the
        // one it has unless a target comes with the save.
        if (value.bidStrategy === 'DYNAMIC') {
          bidSpecification.optimiseFor = biddingEventTargetOf(value.biddingGoal)
        }
      }

      // A budget split over periods replaces the single BUDGET objective —
      // the API rejects a line item that carries both.
      const periods = value.budget
      const objectives: LineItemObjectives = {}
      for (const type of OBJECTIVE_TYPES) {
        const raw = value.objectives[type]
        if (!raw) continue
        if (type === 'BUDGET') {
          if (periods.length > 0) continue
          const amount = parseFloat(raw)
          if (!isNaN(amount)) objectives.BUDGET = { amount, currency }
        } else {
          const count = parseInt(raw)
          if (!isNaN(count)) objectives[type] = count
        }
      }

      const budgetDates = budgetFlight(periods, timeZone)

      const input: LineItemInput = {
        name: value.name,
        externalReference: value.externalReference || undefined,
        // Sent as null when cleared, so removing the deal really removes it.
        dealId: value.dealId.trim() || null,
        // The API takes the election as a bare id; null detaches it. A topic
        // without an election means nothing, so it goes when the election does.
        election: value.electionId || null,
        politicalCampaignTopic: value.electionId ? value.politicalCampaignTopic || null : null,
        objectState: (lineItem?.objectState ?? 'ACTIVE') as ObjectState,
        // Left out on a new line item, so the network's own default applies.
        userState: value.userState || undefined,
        order: value.orderId ? { id: value.orderId } : undefined,
        axProduct: value.axProductId || null,
        type: value.type,
        // The periods own the flight when there are any.
        startDate: budgetDates?.startDate ?? (value.startDate || null),
        endDate: budgetDates?.endDate ?? (value.endDate || null),
        budget: budgetPeriodsForSave(periods, currency),
        budgetRollover: value.budgetRollover,
        deliveryCurve: value.deliveryCurve,
        deduplicationLevel: value.creativeDelivery,
        // Equal weighting is the absence of an optimisation, so it is cleared.
        optimiseCreativesFor: value.creativeWeighting === 'CLICK' ? 'CLICK' : null,
        bidSpecification,
        objectives,
        // A creative tag line item is billed in the same currency as its amounts.
        ...(value.type === 'CREATIVE_TAG' && currency ? { billingCurrency: currency } : {}),
        // Always sent, and always whole: the server replaces a line item's
        // targeting with what the save carries, so a partial payload would
        // leave stale targets behind and an absent one would change nothing.
        targeting: targetingForSave(value.targeting),
        rateLimits: rateLimitsForSave(value.rateLimits, currency),
        ...(value.type === 'SPONSORSHIP' && value.sponsorshipPercentage && !isNaN(sponsorship)
          ? { sponsorshipPercentage: sponsorship }
          : {}),
      }
      mutation.mutate({ input, ignoreProduct: ignoreProductRef.current })
    },
  })

  const saveObjectState = useSaveObjectState(
    (objectState) => LineItemsApi.update(id, { name: lineItem?.name ?? '', objectState }),
    ['lineitem', id],
    ['lineitems']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  // Cancel on a saved line item throws the edits away and stays on the page.
  // The type and the product override live beside the form, so they go back by hand.
  function discardChanges() {
    form.reset()
    markClean()
    setLineItemType(form.getFieldValue('type'))
    setIgnoreProductChoice(null)
  }

  // A new line item has nothing to go back to, so cancel leaves page
  function handleCancel() {
    if (!isNew) return discardChanges()
    const orderId = form.getFieldValue('orderId')
    cancelNew(orderId ? `/advertising/orders/${orderId}` : '/advertising/lineitems')
  }

  // A new line item runs for its order's budget — the first period's start to
  // the last one's end — unless it has budget periods of its own.
  function applyOrderFlight(orderId: string) {
    if (!isNew || form.getFieldValue('budget').length > 0) return
    const order = orders.find((o) => o.id === orderId)
    const dates = order && budgetFlight(mapBudgetPeriods(order.budget), timeZone)
    if (!dates) return
    form.setFieldValue('startDate', dates.startDate)
    form.setFieldValue('endDate', dates.endDate)
  }

  // The order a new line item was started from loads after the form does, so
  // its flight is applied once it arrives — unless another order was picked.
  const initialOrderFlightApplied = useRef(false)
  const initialOrderLoaded = orders.some((o) => o.id === initialOrderId)
  useEffect(() => {
    if (initialOrderFlightApplied.current || !initialOrderLoaded) return
    initialOrderFlightApplied.current = true
    if (form.getFieldValue('orderId') === initialOrderId) applyOrderFlight(initialOrderId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, when the order arrives
  }, [initialOrderLoaded])
  const limitText = useFieldLimitText()

  const mutation = useMutation({
    mutationFn: ({ input, ignoreProduct }: { input: LineItemInput; ignoreProduct: boolean }) =>
      LineItemsApi.update(id, input, { ignoreProduct }),
    onSuccess: (data) => {
      if (posthogConfigured) posthog.capture(isNew ? 'line_item_created' : 'line_item_updated', {
        line_item_type: data.type,
        has_order: Boolean(data.orderId),
        is_political: Boolean(data.electionId),
      })
      queryClient.setQueryData(['lineitem', id], data)
      // The save response leaves out budget spend, which only a plain fetch
      // works out, so the budget table refetches it.
      if (data.budget.length) queryClient.invalidateQueries({ queryKey: ['lineitem', id] })
      queryClient.setQueryData(['lineitems'], (old: LineItem[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((li) => li.id === data.id)
        return idx >= 0 ? old.map((li) => (li.id === data.id ? data : li)) : [...old, data]
      })
      markClean()
      if (isNew) navigate(`/advertising/lineitems/${id}`, { replace: !createDialog })
      toast.success(t('common.saved'), {
        description: isNew ? t('lineItems.createdSuccess') : t('lineItems.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      toast.error(isNew ? t('lineItems.createFailed') : t('lineItems.saveFailed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const { data: dataView } = useQuery({
    queryKey: ['lineitem-dataview', id],
    queryFn: () => DataViewApi.fetchOne<LineItemDataView>('LineItemView', id),
    enabled: !!id && !isNew,
    staleTime: 5 * 60 * 1000,
  })

  const teamId = dataView?.team
    ? ((dataView.team as Record<string, unknown>).id as string | undefined)
    : undefined
  const advertiserIdFromView = dataView?.advertiser
    ? ((dataView.advertiser as Record<string, unknown>).id as string | undefined)
    : undefined
  const advertiserId = lineItem?.advertiserId || advertiserIdFromView
  const advertiserName =
    lineItem?.advertiserName ||
    (dataView?.advertiser
      ? ((dataView.advertiser as Record<string, unknown>).name as string | undefined)
      : undefined)

  // A line item that has already begun keeps the start it ran with.
  const hasStarted = !isNew && !!lineItem?.startDate && new Date(lineItem.startDate) <= new Date()

  // New line items have no data view yet — fall back to the picked order's owners.
  const selectedOrderId = useStore(form.store, (s) => s.values.orderId)
  const selectedOrder = orders.find((o) => o.id === selectedOrderId)
  const defaultCurrency = useDefaultCurrency({
    advertiserId: advertiserId || selectedOrder?.advertiserId,
    teamId: teamId || selectedOrder?.teamId,
  })
  // What the amount fields display: the explicit pick, else the derived default.
  const pickedCurrency = useStore(form.store, (s) => s.values.currency)
  // The budget's periods, when it has any — they drive the flight and replace
  // the single BUDGET objective.
  const budgetPeriods = useStore(form.store, (s) => s.values.budget)
  const budgetRollover = useStore(form.store, (s) => s.values.budgetRollover)
  const hasBudgetPeriods = budgetPeriods.length > 0
  // Opens on the kind of budget the line item has; saving periods moves to them.
  const [budgetMode, setBudgetMode] = useState<BudgetMode>(() =>
    (lineItem?.budget?.length ?? 0) > 0 ? 'periods' : 'single'
  )
  // Delivery is still reported against BUDGET when the budget is split over
  // periods, so the progress card is given their sum as the target it has none
  // of its own for.
  const progressObjectives = useMemo(() => {
    const saved = lineItem?.budget ?? []
    if (saved.length === 0) return lineItem?.objectives
    return { ...lineItem?.objectives, BUDGET: budgetTotal(saved) }
  }, [lineItem?.objectives, lineItem?.budget])
  // What both the report card and the ended-flight band report on.
  const reportTarget: ReportTarget | undefined = lineItem
    ? {
        type: 'lineItem',
        id: lineItem.id,
        name: lineItem.name,
        startDate: lineItem.startDate,
        endDate: lineItem.endDate,
      }
    : undefined
  const pickedProductId = useStore(form.store, (s) => s.values.axProductId)

  // Product-backed line items are marketplace ones; so is everything on a
  // marketplace-only network. Each reads its own allowed-field settings, which
  // the booked product then narrows further.
  const isMarketplaceLineItem =
    !!pickedProductId ||
    (!!currentNetwork?.marketplacePlatform && !currentNetwork?.hybridMarketplacePlatform)
  const networkFields = useLineItemFieldSettings(isMarketplaceLineItem)
  // Only a marketplace network sells products; a plain ad server account has
  // none to book a line item against.
  const hasProducts = !!currentNetwork?.marketplacePlatform
  const { data: products = [] } = useQuery({
    queryKey: AX_PRODUCTS_QUERY_KEY,
    queryFn: () => AxProductsApi.getAll(),
    staleTime: 5 * 60 * 1000,
    enabled: hasProducts,
  })
  const pickedProduct = products.find((p) => p.id === pickedProductId)
  const constraints = applyProductConstraints(networkFields, pickedProduct)
  const productLimits = productTargetingLimits(pickedProduct)

  // A political line item declares the election it advertises for and the topic
  // within that election its message is about. The fields show for a political
  // product, on a network running the political app, and on any line item that
  // already carries an election — so an existing one never hides its own data.
  const pickedElectionId = useStore(form.store, (s) => s.values.electionId)
  const isPoliticalLineItem =
    pickedProduct?.type === 'POLITICAL' || !!currentNetwork?.politicalApp || !!pickedElectionId
  // A political ad that has started delivering keeps the targeting it was
  // published with — the transparency record has to match what was shown.
  const politicalTargetingLocked =
    !!lineItem &&
    (!!lineItem.electionId || pickedProduct?.type === 'POLITICAL') &&
    ((lineItem.renderedDelivery ?? 0) > 0 || (lineItem.impressionDelivery ?? 0) > 0)
  // A new line item has no data view yet, so the picked order names the advertiser.
  const {
    topicsByElection,
    linkedElectionIds,
    campaigns: linkedCampaigns,
    isLoading: topicsLoading,
  } = useElectionTopics(
    advertiserId || selectedOrder?.advertiserId,
    isPoliticalLineItem
  )
  const electionTopicCounts = useMemo(() => topicCounts(topicsByElection), [topicsByElection])
  const electionTopics = topicsByElection[pickedElectionId] ?? []
  // A line item for a political advertiser is marked as political, as orders are.
  const badgeAdvertiserId = advertiserId || selectedOrder?.advertiserId
  const { data: lineItemAdvertiser } = useQuery({
    queryKey: ['advertiser', badgeAdvertiserId],
    queryFn: () => AdvertisersApi.getById(badgeAdvertiserId!),
    enabled: !!badgeAdvertiserId,
  })
  const hasPoliticalAdvertiser = !!lineItemAdvertiser?.advertiserType

  // A line item that already targets something the product does not allow was
  // saved past the product before, so default to doing that again rather than
  // letting this save quietly discard that targeting.
  const targetingNeedsProductOverride =
    !!productLimits &&
    typesInUse(lineItem?.targeting).some(
      (type) =>
        !productLimits.mandatoryTypes.includes(type) && !productLimits.optionalTypes.includes(type)
    )
  const ignoreProduct = ignoreProductChoice ?? targetingNeedsProductOverride
  ignoreProductRef.current = ignoreProduct

  // A political product sells political ads, and a political ad has to name
  // the election it is for. The backend does not enforce this, so we do.
  const electionRequired = ({ value }: { value: string }) => {
    const product = products.find((p) => p.id === form.getFieldValue('axProductId'))
    return product?.type === 'POLITICAL' && !value ? t('political.electionRequired') : undefined
  }

  function handleProductChange(setField: (v: string) => void, productId: string) {
    setField(productId)
    // Only re-check an election already flagged, so picking a political
    // product does not raise the error before the user has had a go.
    if (form.getFieldMeta('electionId')?.errors.length) form.validateField('electionId', 'change')
    const product = products.find((p) => p.id === productId)
    if (!product) return
    // The product dictates the line item type, exactly as the backend would.
    const type = lineItemTypeForProduct(product)
    form.setFieldValue('type', type)
    setLineItemType(type)
    // A FIXED product prices the CPM for you.
    if (product.cpmSpecification === 'FIXED' && product.minimumCpm) {
      form.setFieldValue('bids.cpm', String(product.minimumCpm.amount))
    }
    // The product may not allow the strategy the line item had picked.
    const allowed = bidStrategiesForProduct(product)
    if (allowed.length && !allowed.includes(form.state.values.bidStrategy)) {
      form.setFieldValue('bidStrategy', allowed[0])
    }
  }
  // A sponsorship buys a share of voice rather than bidding, so it has no
  // strategy to pick; otherwise the product's `bidStrategyOption` decides.
  const bidStrategyChoices = lineItemType === 'SPONSORSHIP' ? [] : constraints.bidStrategies
  // A product that fixes the CPM fixes it in its own currency. Otherwise the
  // explicit pick, else the default derived from the advertiser/team — the
  // backend converts amounts to the product's currency when checking them
  // against its limits.
  const productFixesCurrency = !!constraints.bidLimits.cpm?.fixed && !!constraints.currency
  // Once a line item has delivered, its spend is counted in its currency.
  const hasDelivered =
    (lineItem?.renderedDelivery ?? 0) > 0 || (lineItem?.impressionDelivery ?? 0) > 0
  const currency =
    (productFixesCurrency ? constraints.currency : pickedCurrency) || defaultCurrency
  currencyRef.current = currency

  // The fees charged against the gross CPM, and the net CPM left to bid with.
  // The gross and the fees set here come from the form, not the saved line
  // item, so the net tracks what is being edited. The fees the server derives
  // only change on save.
  const cpmEntered = useStore(form.store, (s) => s.values.bids.cpm)
  const grossCpm = useMemo(() => {
    const amount = parseFloat(cpmEntered)
    return isNaN(amount) || !currency ? undefined : { amount, currency }
  }, [cpmEntered, currency])
  const cpmFees = useStore(form.store, (s) => s.values.cpmFees)
  const supplementaryFees = useStore(form.store, (s) => s.values.supplementaryFees)
  const bidWithFees = useMemo(
    () => ({ ...lineItem?.bidSpecification, cpmFees, supplementaryFees }),
    [lineItem?.bidSpecification, cpmFees, supplementaryFees]
  )
  const cpmBreakdown = useCpmBreakdown(id, bidWithFees, grossCpm)
  /** Whether the fee arithmetic under the CPM field is expanded. */
  // A product's CPM floor is a net price, so the minimum shown on the gross CPM
  // field has to carry the fees.
  // The product's limits are in its own currency, whatever the line item is in.
  const limitCurrency = constraints.currency || currency
  const pricedConstraints = grossUpCpmFloor(constraints, cpmBreakdown.fees, limitCurrency)
  const fieldSettings = { ...pricedConstraints, ready: networkFields.ready }
  const biddingCustomEvents = useBiddingCustomEvents(
    fieldSettings.biddingCustomEvents,
    lineItem?.bidSpecification?.optimiseFor?.customEventType?.id
  )
  // Budget sits in its own section above, so the goals row holds the rest.
  const goalObjectives = fieldSettings.objectives.filter((type) => type !== 'BUDGET')

  // With several bids or goals on offer, only those in use are shown and the
  // rest wait behind an "add" menu. A field is in use once it holds a value or
  // has been added; a lone choice is always shown.
  const bidValues = useStore(form.store, (s) => s.values.bids)
  const objectiveValues = useStore(form.store, (s) => s.values.objectives)
  const [addedBids, setAddedBids] = useState<BidField[]>([])
  const [addedGoals, setAddedGoals] = useState<ObjectiveType[]>([])
  // A fixed CPM, or fees charged against it, keep the CPM in view.
  const bidPinned = (f: BidField) =>
    fieldSettings.bidFields.length === 1 ||
    (f === 'cpm' && (!!fieldSettings.bidLimits.cpm?.fixed || cpmBreakdown.fees.length > 0))
  const goalPinned = () => goalObjectives.length === 1
  const shownBids = fieldSettings.bidFields.filter(
    (f) => bidPinned(f) || addedBids.includes(f) || !!bidValues[f]
  )
  const shownGoals = goalObjectives.filter(
    (type) => goalPinned() || addedGoals.includes(type) || !!objectiveValues[type]
  )
  const addBid = (f: BidField) => setAddedBids((fields) => [...fields, f])
  const addGoal = (type: ObjectiveType) => setAddedGoals((types) => [...types, type])
  const removeBid = (f: BidField) => {
    form.setFieldValue(`bids.${f}`, '')
    setAddedBids((fields) => fields.filter((x) => x !== f))
  }
  const removeGoal = (type: ObjectiveType) => {
    form.setFieldValue(`objectives.${type}`, '')
    setAddedGoals((types) => types.filter((x) => x !== type))
  }

  // The picker lives inside every amount field; changing it anywhere changes
  // the one currency shared by all of them. It shows the currency the save
  // will use, so a derived default reads as set rather than as a placeholder.
  const currencyControl = {
    currency,
    onCurrencyChange: (v: string) => form.setFieldValue('currency', v),
    currencyDisabled: productFixesCurrency || hasDelivered,
  }
  // The limits only bound the inputs while the amounts are in the same currency.
  const limitsApply = currency === limitCurrency

  const crumbs = useObjectCrumbs(
    {
      advertiser: { id: advertiserId, name: advertiserName },
      order: { id: lineItem?.orderId, name: lineItem?.orderName },
    },
    isNew ? t('lineItems.newLineItem') : (lineItem?.name ?? t('lineItems.editTitle'))
  )

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={lineItem?.validationWarnings} />
        <PoliticalLinkStatus
          advertiser={lineItemAdvertiser}
          political={!!pickedElectionId}
          campaigns={linkedCampaigns}
          electionId={pickedElectionId}
        />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.lineItem')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('lineItems.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {lineItem && !isNew && (
            <div className="ad-page__actions">
              <TagLineItemButton lineItemId={lineItem.id} />
              <VersionHistoryButton />
              {canCopy && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  title={t('lineItems.copyTitle')}
                  aria-label={t('lineItems.copyTitle')}
                  onClick={() => setCopyOpen(true)}
                >
                  <Copy className="size-4" />
                </Button>
              )}
              <ObjectStateToggle value={lineItem!.objectState} onChange={saveObjectState} allowDelete={!isMarketplaceLineItem} />
            </div>
          )}
        </div>

        {lineItem && !isNew && canCopy && (
          <LineItemCopyDialog lineItem={lineItem} open={copyOpen} onOpenChange={setCopyOpen} />
        )}

        {lineItem && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={lineItem.id} />,
              },
              {
                label: t('stats.activity'),
                content: <StatsBarActivity created={formatDate(lineItem.createTime)} />,
              },
            ]}
          />
        )}

        {lineItem && !isNew && (
          <ListTabs
            variant="line"
            tabs={[
              {
                key: 'overview',
                label: t('lineItems.pageTabs.overview'),
                icon: <LayoutDashboard />,
              },
              { key: 'stats', label: t('lineItems.pageTabs.stats'), icon: <ChartLine /> },
              {
                key: 'diagnostics',
                label: t('lineItems.pageTabs.diagnostics'),
                icon: <Stethoscope />,
              },
            ]}
            value={pageTab}
            onValueChange={setPageTab}
          />
        )}

        <div
          className={`flex flex-col gap-3.5 lg:flex-row lg:items-stretch${pageTab === 'overview' ? '' : ' hidden'}`}
        >
          {/* Left: form cards */}
          <div className="flex flex-1 min-w-0 flex-col gap-3.5">
            {/* Details card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={IdCard} />
                  {t('common.details')}
                  {hasPoliticalAdvertiser && <PoliticalBadge />}
                </CardTitle>
                <CardAction>
                  <form.Field name="userState">
                    {(field) => (
                      <UserStateSelect
                        value={field.state.value}
                        onChange={(s) => field.handleChange(s)}
                        size="md"
                        states={isMarketplaceLineItem ? MARKETPLACE_USER_STATES : undefined}
                      />
                    )}
                  </form.Field>
                </CardAction>
              </CardHeader>
              {lineItem && !isNew && (
                <FlightOutcomeBand
                  startDate={lineItem.startDate}
                  endDate={lineItem.endDate}
                  executionState={lineItem.executionState}
                  deliveryPct={lineItem.deliveryPercentage}
                  elapsedTimePct={lineItem.elapsedTimePercentage}
                  objectives={progressObjectives}
                  objectiveDelivery={lineItem.objectiveDelivery}
                  reportTarget={isMarketplaceLineItem ? undefined : reportTarget}
                />
              )}
              <CardContent>
                <div className="field-grid">
                  {/* The product constrains most of what follows, so it leads. */}
                  {hasProducts && (
                    <div className="p-field full">
                      <label>{t('lineItems.marketplaceProduct')}</label>
                      <form.Field name="axProductId">
                        {(field) => (
                          <MarketplaceProductSelect
                            value={field.state.value}
                            onChange={(v) => handleProductChange(field.handleChange, v)}
                            defaultOpen={isNew}
                            className="w-full"
                          />
                        )}
                      </form.Field>
                    </div>
                  )}

                  <div className="p-field">
                    <label>{t('lineItems.order')}</label>
                    <form.Field name="orderId">
                      {(field) => (
                        <SearchSelect
                          value={field.state.value}
                          onChange={(v) => {
                            field.handleChange(v)
                            applyOrderFlight(v)
                          }}
                          options={orders.map((o) => ({ value: o.id, label: o.name }))}
                          fallbackLabel={lineItem?.orderName}
                          placeholder={t('lineItems.selectOrder')}
                          searchPlaceholder={t('lineItems.searchOrders')}
                          emptyText={t('orders.noOrdersFound')}
                          icon={ClipboardList}
                        />
                      )}
                    </form.Field>
                  </div>

                  <div className="p-field">
                    <label>{t('lineItems.externalReference')}</label>
                    <form.Field name="externalReference">
                      {(field) => (
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('lineItems.externalReferencePlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>

                  {/* A marketplace line item takes its type from the product. */}
                  {!isMarketplaceLineItem && (
                    <div className="p-field">
                      <FieldLabel>{t('lineItems.type')}</FieldLabel>
                      <form.Field name="type">
                        {(field) => (
                          <Select
                            label={t('lineItems.type')}
                            value={field.state.value}
                            onValueChange={(v) => {
                              const typ = v as LineItemType
                              field.handleChange(typ)
                              setLineItemType(typ)
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue>{t(`lineItems.types.${field.state.value}`)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {LINE_ITEM_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {t(`lineItems.types.${type}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                  )}

                  {lineItemType === 'SPONSORSHIP' && (
                    <div className="p-field full">
                      <FieldLabel tooltip={t('lineItems.shareOfVoice.help')}>
                        {t('lineItems.sponsorshipPercentage')}
                      </FieldLabel>
                      <form.Field name="sponsorshipPercentage">
                        {(field) => (
                          <ShareOfVoiceInput
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            label={t('lineItems.sponsorshipPercentage')}
                            summary={(share) =>
                              share >= 100
                                ? t('lineItems.shareOfVoice.all')
                                : t('lineItems.shareOfVoice.split', {
                                    share: formatNumber(share),
                                    rest: formatNumber(100 - share),
                                  })
                            }
                          />
                        )}
                      </form.Field>
                    </div>
                  )}

                  {/* Only line items that trade on a deal carry one, so the
                      field stays behind an "add" button until it is needed. */}
                  <form.Field name="dealId">
                    {(field) => (
                      <OptionalField
                        className="full"
                        label={t('lineItems.dealId')}
                        tooltip={t('lineItems.dealIdHelp')}
                        addLabel={t('lineItems.addDealId')}
                        hasValue={!!field.state.value}
                        onClear={() => field.handleChange('')}
                      >
                        <Input
                          autoFocus={!field.state.value}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('lineItems.dealIdPlaceholder')}
                        />
                      </OptionalField>
                    )}
                  </form.Field>
                </div>
              </CardContent>
            </Card>

            {/* Political card — only for a political line item, which has to
                declare the election it advertises for and the topic its
                message is about. */}
            {isPoliticalLineItem && (
              <Card tone="attention">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Flag} />
                    {t('political.politicalContext')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field">
                      <FieldLabel tooltip={t('political.electionHelp')}>
                        {t('political.election')}
                      </FieldLabel>
                      <form.Field name="electionId" validators={{ onChange: electionRequired, onSubmit: electionRequired }}>
                        {(field) => (
                          <form.Field name="politicalCampaignTopic">
                            {(topicField) => (
                              <ElectionSelect
                                value={field.state.value}
                                invalid={field.state.meta.errors.length > 0}
                                fallbackName={lineItem?.electionName}
                                topicCounts={electionTopicCounts}
                                electionIds={linkedElectionIds}
                                onChange={(electionId) => {
                                  field.handleChange(electionId)
                                  // The topics belong to the election, so a
                                  // topic from the old one cannot stand.
                                  if (electionId !== field.state.value) {
                                    topicField.handleChange('')
                                  }
                                }}
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Field>
                      <form.Subscribe selector={(s) => s.fieldMeta.electionId?.errors[0]}>
                        {(error) =>
                          error && <p className="text-xs text-destructive">{error as string}</p>
                        }
                      </form.Subscribe>
                    </div>

                    <div className="p-field">
                      <FieldLabel
                        tooltip={t('political.electionTopicHelp')}
                        hint={
                          pickedElectionId
                            ? t('political.topicCount', { count: electionTopics.length })
                            : undefined
                        }
                      >
                        {t('political.electionTopic')}
                      </FieldLabel>
                      <form.Field name="politicalCampaignTopic">
                        {(field) => (
                          <ElectionTopicSelect
                            value={field.state.value}
                            onChange={field.handleChange}
                            topics={electionTopics}
                            loading={topicsLoading}
                            disabled={!pickedElectionId}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bidding & objectives card */}
            <Card tone="money">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={TrendingUp} />
                  {t('lineItems.biddingAndObjectives')}
                </CardTitle>
                {/* Marketplace can split the budget over periods. The choice
                    shapes the whole card — with periods the dates go too — so
                    it sits in the header; it opens on what the line item uses. */}
                {fieldSettings.ready &&
                  fieldSettings.objectives.includes('BUDGET') &&
                  isMarketplaceLineItem && (
                    <CardAction>
                      <Tabs value={budgetMode} onValueChange={(v) => setBudgetMode(v as BudgetMode)}>
                        <TabsList aria-label={t('lineItems.objectiveTypes.BUDGET')}>
                          <TabsTrigger value="single">{t('budgets.modeSingle')}</TabsTrigger>
                          <TabsTrigger value="periods">{t('budgets.modePeriods')}</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </CardAction>
                  )}
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {!fieldSettings.ready && (
                  <div className="field-grid field-grid--3-responsive">
                    <FieldSkeleton count={3} />
                  </div>
                )}

                {/* A budget split over periods owns the flight — the API takes
                    the first period's start and the last one's end, and nothing
                    else — so with that tab chosen the pickers step aside and
                    the periods table carries the dates. */}
                {budgetMode !== 'periods' && (
                  <div className="field-grid">
                    <div className="p-field">
                      <FieldLabel tooltip={hasStarted ? t('lineItems.startDateLocked') : undefined}>
                        {t('common.startDate')}
                      </FieldLabel>
                      <form.Field name="startDate">
                        {(field) => (
                          <DateTimePicker
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            placeholder={t('lineItems.selectStartDate')}
                            disabled={hasStarted}
                            fromDate={hasStarted ? undefined : startOfToday()}
                          />
                        )}
                      </form.Field>
                    </div>

                    <div className="p-field">
                      <label>{t('common.endDate')}</label>
                      <form.Field name="startDate">
                        {(startField) => (
                          <form.Field name="endDate">
                            {(field) => (
                              <DateTimePicker
                                value={field.state.value}
                                onChange={(v) => field.handleChange(v)}
                                placeholder={t('lineItems.selectEndDate')}
                                defaultTime="end"
                                fromDate={
                                  startField.state.value
                                    ? new Date(startField.state.value)
                                    : undefined
                                }
                              />
                            )}
                          </form.Field>
                        )}
                      </form.Field>
                    </div>
                  </div>
                )}

                {/* The budget, after the flight it is spent over. Marketplace
                    can split it over periods instead; everywhere else it is
                    the single figure the objective carries. */}
                {fieldSettings.ready && fieldSettings.objectives.includes('BUDGET') && (
                  <div className="p-field">
                    <FieldLabel
                      hint={
                        budgetMode === 'periods'
                          ? undefined
                          : limitText(fieldSettings.budgetLimits, limitCurrency)
                      }
                    >
                      {t('lineItems.objectiveTypes.BUDGET')}
                    </FieldLabel>
                    {budgetMode === 'periods' ? (
                      <div className="flex flex-col gap-2">
                        {!hasBudgetPeriods && (
                          <p className="text-sm text-muted-foreground">{t('budgets.noPeriods')}</p>
                        )}
                        <BudgetPeriodsTable
                          periods={budgetPeriods}
                          rollover={budgetRollover}
                          onEdit={() => setBudgetDialogOpen(true)}
                          editLabel={hasBudgetPeriods ? undefined : t('budgets.addPeriods')}
                        />
                      </div>
                    ) : (
                      <div className="flex w-full max-w-64 flex-col items-start gap-2">
                        <form.Field name="objectives.BUDGET">
                          {(field) => (
                            <CurrencyAmountInput
                              value={field.state.value}
                              onChange={(v) => {
                                field.handleChange(v)
                                // Periods win on save, so a single budget only
                                // counts once they are gone.
                                if (v && hasBudgetPeriods) form.setFieldValue('budget', [])
                              }}
                              min={limitsApply ? fieldSettings.budgetLimits.min : undefined}
                              max={limitsApply ? fieldSettings.budgetLimits.max : undefined}
                              className="w-full"
                              {...currencyControl}
                            />
                          )}
                        </form.Field>
                        {hasBudgetPeriods && (
                          <p className="text-xs text-(--ad-text-muted)">
                            {t('budgets.singleReplacesPeriods')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* A creative tag line item is billed in the currency of its
                    amounts, so this sets the same currency as their pickers. */}
                {fieldSettings.ready && lineItemType === 'CREATIVE_TAG' && (
                  <div className="field-grid field-grid--3-responsive">
                    <div className="p-field">
                      <FieldLabel tooltip={t('lineItems.billingCurrencyHelp')}>
                        {t('lineItems.billingCurrency')}
                      </FieldLabel>
                      <CurrencySelect
                        value={currency}
                        onValueChange={currencyControl.onCurrencyChange}
                        label={t('lineItems.billingCurrency')}
                        disabled={currencyControl.currencyDisabled}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}

                {/* The prices bid per event, kept apart from the volume goals. */}
                {fieldSettings.ready && fieldSettings.bidFields.length > 0 && (
                  <>
                    <Separator />
                    {shownBids.length > 0 && (
                      <div className="field-grid field-grid--3-responsive">
                        {shownBids.map((bidField) => (
                          <div className="p-field" key={bidField}>
                            <div className="flex items-center justify-between gap-2">
                              <FieldLabel
                                hint={limitText(fieldSettings.bidLimits[bidField], limitCurrency)}
                              >
                                {/* Once fees are charged, the CPM entered here is the gross one. */}
                                {bidField === 'cpm' && cpmBreakdown.fees.length > 0
                                  ? t('fees.grossCpm')
                                  : t(`lineItems.bidFields.${bidField}`)}
                              </FieldLabel>
                              {!bidPinned(bidField) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-xs"
                                  aria-label={t('common.remove')}
                                  onClick={() => removeBid(bidField)}
                                >
                                  <Trash2 />
                                </Button>
                              )}
                            </div>
                            <form.Field name={`bids.${bidField}`}>
                              {(field) => (
                                <CurrencyAmountInput
                                  value={field.state.value}
                                  onChange={(v) => {
                                    field.handleChange(v)
                                    if (!addedBids.includes(bidField)) addBid(bidField)
                                  }}
                                  min={limitsApply ? fieldSettings.bidLimits[bidField]?.min : undefined}
                                  disabled={fieldSettings.bidLimits[bidField]?.fixed}
                                  {...currencyControl}
                                />
                              )}
                            </form.Field>
                            {/* What the fees leave of that gross CPM to bid with —
                                tucked under the input it derives from. */}
                            {bidField === 'cpm' && (
                              <CpmFeeBreakdown
                                breakdown={cpmBreakdown}
                                onEditFees={() => setFeesDialogOpen(true)}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <AddFieldMenu
                      label={t('lineItems.addBid')}
                      options={fieldSettings.bidFields
                        .filter((f) => !shownBids.includes(f))
                        .map((f) => ({ value: f, label: t(`lineItems.bidFields.${f}`) }))}
                      onAdd={addBid}
                    />
                  </>
                )}

                {fieldSettings.ready && goalObjectives.length > 0 && (
                  <>
                    <Separator />
                    {shownGoals.length > 0 && (
                      <div className="field-grid field-grid--3-responsive">
                        {shownGoals.map((type) => (
                          <div className="p-field" key={type}>
                            <div className="flex items-center justify-between gap-2">
                              <FieldLabel>{t(`lineItems.objectiveTypes.${type}`)}</FieldLabel>
                              {!goalPinned() && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-xs"
                                  aria-label={t('common.remove')}
                                  onClick={() => removeGoal(type)}
                                >
                                  <Trash2 />
                                </Button>
                              )}
                            </div>
                            <form.Field name={`objectives.${type}`}>
                              {(field) => (
                                <Input
                                  type="number"
                                  min={0}
                                  value={field.state.value}
                                  onChange={(e) => {
                                    field.handleChange(e.target.value)
                                    if (!addedGoals.includes(type)) addGoal(type)
                                  }}
                                  placeholder={t('lineItems.noObjective')}
                                />
                              )}
                            </form.Field>
                          </div>
                        ))}
                      </div>
                    )}
                    <AddFieldMenu
                      label={t('lineItems.addObjective')}
                      options={goalObjectives
                        .filter((type) => !shownGoals.includes(type))
                        .map((type) => ({
                          value: type,
                          label: t(`lineItems.objectiveTypes.${type}`),
                        }))}
                      onAdd={addGoal}
                    />
                  </>
                )}

                {/* How the bidding and delivery behave, once the numbers are set. */}
                <Separator />
                <div className="field-grid field-grid--3-responsive">
                  {fieldSettings.ready && (
                    <form.Field name="bidStrategy">
                      {(strategyField) => (
                        <form.Field name="biddingGoal">
                          {(goalField) => (
                            <BidStrategyFields
                              strategy={strategyField.state.value}
                              onStrategyChange={(v) => strategyField.handleChange(v)}
                              goal={goalField.state.value}
                              onGoalChange={(v) => goalField.handleChange(v)}
                              strategies={bidStrategyChoices}
                              customEvents={biddingCustomEvents}
                            />
                          )}
                        </form.Field>
                      )}
                    </form.Field>
                  )}

                  <div className="p-field">
                    <label>{t('lineItems.deliveryCurve')}</label>
                    <form.Field name="deliveryCurve">
                      {(field) => (
                        <Select
                          label={t('lineItems.deliveryCurve')}
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v as DeliveryCurve)}
                        >
                          <SelectTrigger>
                            <SelectValue>
                              {t(`lineItems.deliveryCurves.${field.state.value}`)}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {fieldSettings.smoothingChoices.map((curve) => (
                              <SelectItem key={curve} value={curve}>
                                {t(`lineItems.deliveryCurves.${curve}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>

                </div>
              </CardContent>
            </Card>

            <form.Field
              name="rateLimits"
              validators={{
                onSubmit: ({ value }) =>
                  hasRateLimitProblem(value) ? t('lineItems.capping.errors.invalid') : undefined,
              }}
            >
              {(field) => (
                <div className="flex flex-col gap-1.5">
                  <CappingCard
                    // A product's own caps replace the line item's server-side,
                    // so those are what the line item actually runs with.
                    value={constraints.productRateLimits ?? field.state.value}
                    onChange={(limits) => field.handleChange(limits)}
                    types={fieldSettings.cappingTypes}
                    defaultScope={fieldSettings.cappingScope}
                    alias={fieldSettings.cappingAlias}
                    // Marketplace-only networks have no line item-wide scope.
                    perUserOnly={
                      !!currentNetwork?.marketplacePlatform &&
                      !currentNetwork?.hybridMarketplacePlatform
                    }
                    readOnly={!!constraints.productRateLimits}
                    readOnlyReason={
                      constraints.productRateLimits && pickedProduct
                        ? t('lineItems.capping.fromProduct', { name: pickedProduct.name })
                        : undefined
                    }
                    currency={currency}
                    currencyControl={currencyControl}
                  />
                  {field.state.meta.errors[0] && (
                    <p className="text-xs text-destructive">
                      {field.state.meta.errors[0] as string}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="targeting">
              {(field) => (
                <TargetingCard
                  targeting={field.state.value}
                  onChange={(targeting) => field.handleChange(targeting)}
                  hiddenTypes={hiddenTargetingTypes(currentNetwork)}
                  lockedReason={politicalTargetingLocked ? t('targeting.politicalLocked') : undefined}
                  product={
                    productLimits && pickedProduct
                      ? { ...productLimits, name: pickedProduct.name }
                      : undefined
                  }
                  ignoreProduct={ignoreProduct}
                  onIgnoreProductChange={canIgnoreProduct ? setIgnoreProductChoice : undefined}
                  // Applying targeting saves the line item there and then. A
                  // line item that does not exist yet is left to the save bar,
                  // where the rest of what a save needs is still being filled
                  // in.
                  onApplied={isNew ? undefined : () => form.handleSubmit()}
                />
              )}
            </form.Field>
          </div>

          {/* Right: stats panel */}
          {!isNew && lineItem && (
            /* Stacked on mobile the column dissolves (`contents`) so its cards can
               order themselves around the form: chart first, the rest last. */
            <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-4">
              <div className="order-first lg:order-none">
                <StatsPanel
                  query={{ lineItemId: lineItem.id, groupBy: 'HALF_HOURLY' }}
                  storageKey={`line-item-stats-${lineItem.id}`}
                  variant="advertising"
                  range={statsRange}
                  onRangeChange={setStatsRange}
                  auditObject={{ type: 'lineItem', id: lineItem.id }}
                  flight={{ startDate: lineItem.startDate, endDate: lineItem.endDate }}
                />
              </div>
              <div className="order-last flex flex-col gap-3.5 lg:order-none lg:gap-4">
                <DeliveryProgressCard
                  startDate={lineItem.startDate}
                  endDate={lineItem.endDate}
                  executionState={lineItem.executionState}
                  deliveryPct={lineItem.deliveryPercentage}
                  elapsedTimePct={lineItem.elapsedTimePercentage}
                  objectives={progressObjectives}
                  objectiveDelivery={lineItem.objectiveDelivery}
                  deliveryCurve={deliveryCurveOf(lineItem)}
                  surface="sunken"
                />
                <ReportsCard target={reportTarget!} surface="sunken" />
              </div>
            </div>
          )}
        </div>

        {!isNew && lineItem && pageTab === 'stats' && (
          <form.Subscribe selector={(state) => state.values.targeting}>
            {(targeting) => (
              <DeliveryBreakdownCard
                query={{ lineItemId: lineItem.id }}
                storageKey={`line-item-${lineItem.id}`}
                range={resolvedStatsRange}
                adUnitTargeting={{
                  targeting,
                  // Saved there and then, as applying targeting in its card is.
                  onChange: (next) => {
                    form.setFieldValue('targeting', next)
                    form.handleSubmit()
                  },
                  saving: mutation.isPending,
                  lockedReason: politicalTargetingLocked
                    ? t('targeting.politicalLocked')
                    : undefined,
                }}
              />
            )}
          </form.Subscribe>
        )}

        {!isNew && lineItem && pageTab === 'diagnostics' && (
          <div className="grid gap-3.5 lg:grid-cols-2 lg:items-start lg:gap-4">
            <form.Subscribe selector={(state) => state.values.targeting}>
              {(targeting) => (
                <LineItemDiagnosticsCard
                  lineItemId={lineItem.id}
                  showBidHistory={lineItem.bidSpecification?.bidStrategy === 'DYNAMIC'}
                  targeting={targeting}
                  creativeSizes={creativeSizes}
                />
              )}
            </form.Subscribe>
            <AuditHistoryCard
              objectType="lineItem"
              objectId={lineItem.id}
              objectName={lineItem.name}
            >
              <NotesSection
                parent="lineItem"
                parentId={lineItem.id}
                noteIds={lineItem.noteIds}
                parentQueryKey={['lineitem', lineItem.id]}
              />
            </AuditHistoryCard>
          </div>
        )}

        {/* Like the diagnostics page it shares its form with, for super admins only. */}
        {!isNew && lineItem && pageTab === 'diagnostics' && isSuperAdmin(currentUser?.username) && (
          <form.Subscribe selector={(state) => state.values.targeting}>
            {(targeting) => (
              <LineItemAdRequestDiagnostics lineItemId={lineItem.id} targeting={targeting} />
            )}
          </form.Subscribe>
        )}

        {/* Save bar */}
        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('lineItems.newLineItem') : t('common.save')}
            </Button>
          </div>
        </div>

        {!isNew && lineItem && pageTab === 'overview' && (
          <form.Field name="creativeWeighting">
            {(weightingField) => (
              <form.Field name="creativeDelivery">
                {(deliveryField) => (
                  <LineItemCreativesPanel
                    lineItem={lineItem}
                    range={statsRange}
                    onRangeChange={setStatsRange}
                    weighting={weightingField.state.value}
                    onWeightingChange={weightingField.handleChange}
                    delivery={deliveryField.state.value}
                    onDeliveryChange={deliveryField.handleChange}
                  />
                )}
              </form.Field>
            )}
          </form.Field>
        )}
      </form>

      {/* Editing the periods moves the line item's own dates with them, so the
          editor hands back both and the save bar carries the lot. */}
      <BudgetPeriodsDialog
        open={budgetDialogOpen}
        onOpenChange={setBudgetDialogOpen}
        periods={budgetPeriods}
        rollover={budgetRollover}
        currency={currency}
        flight={{
          startDate: form.getFieldValue('startDate'),
          endDate: form.getFieldValue('endDate'),
        }}
        seedAmount={parseFloat(form.getFieldValue('objectives.BUDGET')) || undefined}
        objectLabel={t('lineItems.lineItem')}
        onSave={(periods, rollover) => {
          form.setFieldValue('budget', periods)
          form.setFieldValue('budgetRollover', rollover)
          const dates = budgetFlight(periods, timeZone)
          if (dates) {
            form.setFieldValue('startDate', dates.startDate)
            form.setFieldValue('endDate', dates.endDate)
          }
          // The two ways of setting a budget are mutually exclusive.
          if (periods.length > 0) {
            form.setFieldValue('objectives.BUDGET', '')
            setBudgetMode('periods')
          }
        }}
      />
      {/* The fees set on the line item itself; the platform's own are shown
          alongside, read-only. Applied to the form, saved with the rest. */}
      <CpmFeesDialog
        open={feesDialogOpen}
        onOpenChange={setFeesDialogOpen}
        cpmFees={cpmFees}
        supplementaryFees={supplementaryFees}
        derivedFees={cpmBreakdown.fees.filter(
          (fee) => fee.source !== 'CPM' && fee.source !== 'SUPPLEMENTARY'
        )}
        grossCpm={grossCpm}
        currency={currency}
        canEditSupplementary={isAdOps}
        canAddSupplementary={isAdOps && !!currentNetwork?.marketplacePlatform}
        supplementaryLabel={networkFields.supplementaryFeeLabel ?? t('fees.supplementaryFee')}
        onSave={(fees) => {
          form.setFieldValue('cpmFees', fees.cpmFees)
          form.setFieldValue('supplementaryFees', fees.supplementaryFees)
        }}
      />
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ───────────────────────────────────────────────────────────────

export function LineItemEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const initialOrderId = isNew ? (searchParams.get('order') ?? '') : ''
  const hasToken = AuthService.isAuthenticated()

  const {
    data: lineItem,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['lineitem', id],
    queryFn: () => LineItemsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbs = useObjectCrumbs(
    {
      advertiser: { id: lineItem?.advertiserId, name: lineItem?.advertiserName },
      order: { id: lineItem?.orderId, name: lineItem?.orderName },
    },
    isNew ? t('lineItems.newLineItem') : (lineItem?.name ?? t('lineItems.editTitle'))
  )

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbs} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <LineItemEditForm
      key={id}
      lineItem={lineItem ?? null}
      id={id!}
      isNew={isNew}
      initialOrderId={initialOrderId}
    />
  )
}
